import { getPica } from './pica';

export interface CompressImageOptions {
  /** Maximum width in pixels the compressed image may reach. Aspect ratio preserved. */
  maxWidth?: number;
  /** Maximum height in pixels the compressed image may reach. Aspect ratio preserved. */
  maxHeight?: number;
  /** Pica resize quality (0–3). Higher is better but slower. Default 3. */
  quality?: 0 | 1 | 2 | 3;
  /** Output MIME type. Defaults to the input file's type. */
  outputType?: string;
  /** Hard cap for a single image in ms. Default 30000. */
  timeoutMs?: number;
}

const DEFAULT_MAX_DIMENSION = 1920;

/**
 * Generous enough that a huge photo on a slow device finishes normally, but
 * short enough that a wedged decode or worker cannot hold the queue hostage.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Budget for the canvas pica reads its tiles from.
 *
 * The copy costs width * height * 4 bytes *while the decoded bitmap is still
 * alive*, so the real figure to bound is that pair: 2^24 pixels is ~64MB per
 * buffer, ~128MB combined, and a 12MP phone photo still fits at full
 * resolution. At 2^25 the same pair reaches ~256MB, which is exactly the peak
 * the serialised queue exists to avoid. Anything past the budget is scaled
 * down on the way in.
 */
const MAX_SOURCE_CANVAS_PIXELS = 16_777_216;

/**
 * Browsers cap each canvas axis independently of its area — Chrome at 65535,
 * Safari far lower — so a 40000x500 panorama sits inside the pixel budget and
 * still cannot be allocated. 8192 is below every current per-axis limit and
 * far above any output this module produces.
 */
const MAX_SOURCE_CANVAS_DIMENSION = 8192;

/** Both caps together: a canvas is only worth attempting inside them. */
const fitsSourceBudget = (width: number, height: number) =>
  width * height <= MAX_SOURCE_CANVAS_PIXELS &&
  width <= MAX_SOURCE_CANVAS_DIMENSION &&
  height <= MAX_SOURCE_CANVAS_DIMENSION;

const formatMegabytes = (bytes: number) =>
  `${(bytes / 1024 / 1024).toFixed(1)}MB`;

const getScaledDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }
  const ratio = Math.min(maxWidth / originalWidth, maxHeight / originalHeight);
  return {
    width: Math.max(1, Math.round(originalWidth * ratio)),
    height: Math.max(1, Math.round(originalHeight * ratio))
  };
};

/**
 * Dimensions for the canvas pica reads from: the source itself while it fits
 * the budget, otherwise reduced on the way in — to at most twice the output,
 * within the per-axis limit, and never past the pixel budget. All three clamps
 * are needed: `maxWidth`/`maxHeight` belong to the caller and may be larger
 * than the source, so the target size cannot carry the budget on its own.
 */
const getSourceDimensions = (
  bitmap: ImageBitmap,
  targetWidth: number,
  targetHeight: number
) => {
  if (fitsSourceBudget(bitmap.width, bitmap.height)) {
    return { width: bitmap.width, height: bitmap.height };
  }

  const reduced = getScaledDimensions(
    bitmap.width,
    bitmap.height,
    Math.min(targetWidth * 2, MAX_SOURCE_CANVAS_DIMENSION),
    Math.min(targetHeight * 2, MAX_SOURCE_CANVAS_DIMENSION)
  );
  const pixels = reduced.width * reduced.height;
  if (pixels <= MAX_SOURCE_CANVAS_PIXELS) return reduced;

  // Floor, not round, so the result cannot land a pixel over the budget.
  const ratio = Math.sqrt(MAX_SOURCE_CANVAS_PIXELS / pixels);
  return {
    width: Math.max(1, Math.floor(reduced.width * ratio)),
    height: Math.max(1, Math.floor(reduced.height * ratio))
  };
};

/**
 * Copy the decoded bitmap into the canvas pica resizes from.
 *
 * Pica must never be handed the `ImageBitmap` itself. With web workers enabled
 * it crops every 1024px tile via `createImageBitmap(source, x, y, w, h)`, and
 * Chrome applies that crop rect to the *unoriented* pixel data while reporting
 * the EXIF-oriented width and height (crbug.com/1220671). Every tile then
 * comes from the wrong part of the photo, and the output looks like a shuffled
 * mosaic — which is what users saw on any rotated phone photo wider than one
 * tile. Pica guards the same bug with a probe that only covers `Image`
 * elements, so an `ImageBitmap` input slips straight past it. A canvas carries
 * no orientation, so its tiling is exact.
 *
 * A source past the canvas budget is scaled down on the way in: the browser
 * does the bulk reduction and pica still does the final, quality-defining
 * step — the same split pica applies internally for large scale factors.
 */
const createSourceCanvas = (
  bitmap: ImageBitmap,
  targetWidth: number,
  targetHeight: number
): OffscreenCanvas => {
  // Pica reads from a canvas at least as large as its output, so an output
  // past the budget cannot be served without breaking it. Refusing here means
  // the caller keeps the original file, rather than an upscaled copy of a
  // source that had to be shrunk below the output size to fit.
  if (!fitsSourceBudget(targetWidth, targetHeight)) {
    throw new Error(
      `Requested output ${targetWidth}x${targetHeight} exceeds the source canvas budget.`
    );
  }

  const { width, height } = getSourceDimensions(
    bitmap,
    targetWidth,
    targetHeight
  );

  const canvas = new OffscreenCanvas(width, height);
  // `getContext` is typed as the union of every offscreen context, so it needs
  // narrowing — to the 2D context this canvas actually has, rather than to the
  // `CanvasRenderingContext2D` of a DOM canvas.
  const ctx = canvas.getContext(
    '2d'
  ) as OffscreenCanvasRenderingContext2D | null;
  if (!ctx) {
    throw new Error('Failed to acquire 2D context for the compression source.');
  }
  ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, width, height);
  return canvas;
};

/**
 * Drop a canvas's backing store now instead of at the next GC. Zeroing the
 * dimensions is the same trick pica applies to its own temporaries.
 */
const releaseCanvas = (canvas: OffscreenCanvas | null) => {
  if (canvas) canvas.width = canvas.height = 0;
};

const compressImageNow = async (
  file: File,
  options: CompressImageOptions
): Promise<File> => {
  const {
    maxWidth = DEFAULT_MAX_DIMENSION,
    maxHeight = DEFAULT_MAX_DIMENSION,
    quality = 3,
    outputType,
    timeoutMs = DEFAULT_TIMEOUT_MS
  } = options;

  const type = outputType || file.type;
  const startedAt = performance.now();
  let imageBitmap: ImageBitmap | null = null;
  let sourceCanvas: OffscreenCanvas | null = null;
  let targetCanvas: OffscreenCanvas | null = null;
  // Kept separately: the bitmap is released before the resize starts, and a
  // detached ImageBitmap reports 0x0.
  let source: { width: number; height: number } | null = null;
  let abandoned = false;
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;

  // Rejects once the budget is spent. Pica accepts it as `cancelToken` and
  // terminates a running resize; the races below cover the steps that have no
  // cancellation of their own. Every step must settle, because a single
  // never-settling promise would stall every queued compression after it.
  const deadline = new Promise<never>((_resolve, reject) => {
    deadlineTimer = setTimeout(
      () => reject(new Error(`compressImage timed out after ${timeoutMs}ms`)),
      timeoutMs
    );
  });
  // The race may already be over when the timer fires — keep that rejection
  // from surfacing as an unhandled one.
  deadline.catch(() => undefined);

  try {
    // Inside the try so that a browser unable to construct Pica falls back to
    // the original file instead of rejecting and stalling the queue.
    const pica = getPica();

    const decoding = createImageBitmap(file);
    // If the deadline wins the race below, `finally` has already run and
    // nothing else would ever release a bitmap that shows up late.
    decoding.then(
      (bitmap) => {
        if (abandoned) bitmap.close?.();
      },
      () => undefined
    );
    imageBitmap = await Promise.race([decoding, deadline]);
    source = { width: imageBitmap.width, height: imageBitmap.height };
    const { width, height } = getScaledDimensions(
      source.width,
      source.height,
      maxWidth,
      maxHeight
    );

    // See `createSourceCanvas`: pica tiles an ImageBitmap source incorrectly.
    // Built before the target canvas, so a request it has to refuse costs no
    // canvas at all — the decoded bitmap above is already paid for either way.
    sourceCanvas = createSourceCanvas(imageBitmap, width, height);
    // The canvas holds the pixels now, so hand its buffer over and drop ours
    // before the resize — the long step then runs against a single
    // full-resolution buffer instead of two.
    imageBitmap.close?.();
    imageBitmap = null;

    targetCanvas = new OffscreenCanvas(width, height);

    // `cancelToken` stops the actual work — pica aborts its tile loop and
    // stops waiting on the worker. Racing on top of it guarantees that this
    // step settles even if pica ever failed to honour the token.
    await Promise.race([
      pica.resize(sourceCanvas, targetCanvas, {
        quality,
        cancelToken: deadline
      }),
      deadline
    ]);

    // `convertToBlob` is missing from some lib.dom variants, so reach it
    // through a narrow view of the canvas instead of a blanket ts-ignore.
    const encoding = (
      targetCanvas as unknown as {
        convertToBlob: (options: { type: string }) => Promise<Blob>;
      }
    ).convertToBlob({ type });
    // If the deadline wins the race, `finally` releases the canvas while this
    // is still running. Swallow whatever it settles with — the result is
    // already abandoned, and an unhandled rejection would only add noise.
    encoding.catch(() => undefined);
    const blob: Blob = await Promise.race([encoding, deadline]);

    const compressed = new File([blob], file.name, {
      type,
      lastModified: Date.now()
    });

    // Re-encoding can grow a file. The source may have been saved at a lower
    // JPEG quality than the browser's default for convertToBlob, so a
    // detailed photo can come out bigger despite having far fewer pixels.
    // Compressing must never make the upload worse than not compressing.
    if (compressed.size >= file.size) {
      console.info(
        `compressImage: ${file.name} ${formatMegabytes(file.size)} ` +
          `${source.width}x${source.height} → kept original ` +
          `(re-encode produced ${formatMegabytes(compressed.size)} ` +
          `at ${width}x${height})`
      );
      return file;
    }

    // One line per image, so a support case can be read straight off the
    // console. An output size equal to the input means compression fell back
    // to the original — the failure branch below says why.
    console.info(
      `compressImage: ${file.name} ${formatMegabytes(file.size)} ` +
        `${source.width}x${source.height} → ` +
        `${formatMegabytes(compressed.size)} ${width}x${height} ` +
        `(${Math.round(performance.now() - startedAt)}ms)`
    );

    return compressed;
  } catch (error) {
    // Name the input and how far we got: "not decoded" points at
    // createImageBitmap, real dimensions point at the resize or the encode.
    const reached = source ? `${source.width}x${source.height}px` : 'not decoded';
    console.error(
      `compressImage failed for "${file.name}" (${formatMegabytes(
        file.size
      )}, ${reached}), returning original file:`,
      error
    );
    return file;
  } finally {
    abandoned = true;
    clearTimeout(deadlineTimer);
    // A decoded bitmap and a canvas each hold width * height * 4 bytes outside
    // the JS heap until the GC gets around to them. Releasing all three here
    // keeps peak memory flat across a batch instead of leaving it to GC timing.
    // The encoded blob is independent of the canvas it came from.
    imageBitmap?.close?.();
    releaseCanvas(sourceCanvas);
    releaseCanvas(targetCanvas);
  }
};

// Promise<void> documents that the tail must not carry a value. It is not
// enforced here — this project builds with strictNullChecks disabled, so
// `Promise<File | undefined>` still assigns cleanly. The comment below is the
// actual guard.
let queue: Promise<void> = Promise.resolve();

/**
 * Run compressions strictly one after another.
 *
 * Callers fan out over their files (`Promise.all`, upload loops), which would
 * otherwise decode every image at once — a 24MP photo costs ~96MB as a decoded
 * bitmap, so a 20-image selection peaked high enough to kill the tab. Queueing
 * caps the peak at a single bitmap. Throughput barely suffers because a single
 * `pica.resize()` already spreads its tiles across the whole worker pool.
 */
const enqueue = async <T>(task: () => Promise<T>): Promise<T> => {
  const result = queue.then(task, task);
  // Discard both outcomes. `.catch()` alone would pass the fulfilled File
  // through, and this queue is module-global — the last processed file would
  // stay reachable for the rest of the session, which is worst exactly when
  // the fallback returned a huge original.
  queue = result.then(
    () => undefined,
    () => undefined
  );
  return await result;
};

/**
 * Compress and resize an image `File` in the browser using Pica.
 *
 * Aspect ratio is preserved, and images already within the target dimensions
 * are not upscaled. Compressions are serialised across the whole app to keep
 * peak memory flat. On any failure the original file is returned so callers
 * can safely wrap this in an upload pipeline without extra try/catch.
 */
export const compressImage = async (
  file: File,
  options: CompressImageOptions = {}
): Promise<File> =>
  await enqueue(async () => await compressImageNow(file, options));

const IMAGE_MIME_PREFIX = 'image/';

/** True when a `File` is an image type that `compressImage` should handle. */
export const isCompressibleImage = (file: File): boolean =>
  file.type.startsWith(IMAGE_MIME_PREFIX);

/**
 * Convenience wrapper for the common "run compressImage on every image in a
 * batch, pass through anything else" pattern used by upload flows.
 */
export const compressImages = async (
  files: File[],
  options?: CompressImageOptions
): Promise<File[]> =>
  await Promise.all(
    files.map((file) =>
      isCompressibleImage(file) ? compressImage(file, options) : file
    )
  );
